import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle, CheckCircle, XCircle, Search } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface ClosedJob {
    id: string;
    job_number: string;
    invoice_total: number;
    closed_at: string;
    job_date: string;
    status: string;
    lead_id: string | null;
    customer_name: string | null;
    lead_campaign_id: string | null;
    lead_campaign_name: string | null;
    lead_source_name: string | null;
    lead_created_at: string | null;
    lead_status: string | null;
}

type DiagnosticCategory = 'no_lead' | 'no_campaign' | 'lead_date_mismatch' | 'attributed';

interface DiagnosticResult {
    job: ClosedJob;
    category: DiagnosticCategory;
    reason: string;
}

export default function DebugRevenue() {
    const { formatMoney } = useCompanyLocale();
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<DiagnosticResult[]>([]);
    const [summary, setSummary] = useState<Record<DiagnosticCategory, { count: number; revenue: number }> | null>(null);
    const [dateRange, setDateRange] = useState({ from: '', to: '' });

    // Default to yesterday
    useEffect(() => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yStr = yesterday.toISOString().split('T')[0];
        setDateRange({ from: yStr, to: yStr });
    }, []);

    const runDiagnostic = async () => {
        if (!dateRange.from || !dateRange.to) return;
        setLoading(true);
        setResults([]);
        setSummary(null);

        try {
            // 1. Get ALL completed jobs closed in the date range (by job_date — the editable business close date)
            const { data: allJobs, error: jobsError } = await supabase
                .from('jobs')
                .select(`
          id,
          job_number,
          invoice_total,
          closed_at,
          job_date,
          status,
          lead_id,
          customer:customers!jobs_customer_id_fkey (customer_name)
        `)
                .eq('status', 'completed')
                .gte('job_date', dateRange.from)
                .lte('job_date', dateRange.to)
                .order('invoice_total', { ascending: false });

            if (jobsError) throw jobsError;

            if (!allJobs || allJobs.length === 0) {
                console.log("No jobs found for date range. Falling back to most recent 30 completed jobs...");
                const { data: recentJobs, error: recentError } = await supabase
                    .from('jobs')
                    .select(`
              id,
              job_number,
              invoice_total,
              closed_at,
              job_date,
              status,
              lead_id,
              customer:customers!jobs_customer_id_fkey (customer_name)
            `)
                    .eq('status', 'completed')
                    .order('created_at', { ascending: false })
                    .limit(30);

                if (recentError) throw recentError;
                await processJobs(recentJobs || [], true);
                return;
            }

            await processJobs(allJobs, false);
        } catch (err: any) {
            console.error("Diagnostic error:", err);
            alert(`Error: ${err.message || JSON.stringify(err)}`);
        } finally {
            setLoading(false);
        }
    };

    const processJobs = async (allJobs: any[], isFallback: boolean) => {
        // 2. For each job, get its lead + campaign info
        const leadIds = allJobs
            .map(j => j.lead_id)
            .filter((id): id is string => !!id);

        let leadsMap = new Map<string, any>();

        if (leadIds.length > 0) {
            // Fetch in batches to avoid URL length limits
            const batchSize = 50;
            for (let i = 0; i < leadIds.length; i += batchSize) {
                const batch = leadIds.slice(i, i + batchSize);
                const { data: leads, error: leadsError } = await supabase
                    .from('leads')
                    .select(`
            id,
            campaign_id,
            lead_source_id,
            status,
            created_at,
            campaigns (id, name),
            lead_sources (name)
          `)
                    .in('id', batch);

                if (leadsError) throw leadsError;
                leads?.forEach(l => leadsMap.set(l.id, l));
            }
        }

        // 3. Categorize each job
        const diagnostics: DiagnosticResult[] = allJobs.map(job => {
            const customerName = (job.customer as any)?.customer_name || 'Unknown';

            if (!job.lead_id) {
                return {
                    job: {
                        ...job,
                        customer_name: customerName,
                        lead_campaign_id: null,
                        lead_campaign_name: null,
                        lead_source_name: null,
                        lead_created_at: null,
                        lead_status: null,
                    },
                    category: 'no_lead' as DiagnosticCategory,
                    reason: 'Job has no lead_id. It was created directly without a lead.',
                };
            }

            const lead = leadsMap.get(job.lead_id);
            if (!lead) {
                return {
                    job: {
                        ...job,
                        customer_name: customerName,
                        lead_campaign_id: null,
                        lead_campaign_name: null,
                        lead_source_name: null,
                        lead_created_at: null,
                        lead_status: null,
                    },
                    category: 'no_lead' as DiagnosticCategory,
                    reason: `Job references lead ${job.lead_id} but lead not found in DB.`,
                };
            }

            const campaignId = lead.campaign_id;
            const campaignName = lead.campaigns?.name || null;
            const sourceName = lead.lead_sources?.name || null;

            const enrichedJob: ClosedJob = {
                ...job,
                customer_name: customerName,
                lead_campaign_id: campaignId,
                lead_campaign_name: campaignName,
                lead_source_name: sourceName,
                lead_created_at: lead.created_at,
                lead_status: lead.status,
            };

            if (!campaignId) {
                return {
                    job: enrichedJob,
                    category: 'no_campaign' as DiagnosticCategory,
                    reason: `Lead exists but campaign_id is NULL. Source: ${sourceName || 'Unknown'}`,
                };
            }

            // Check if lead was created in a different period than the report range
            const leadDate = lead.created_at?.split('T')[0];
            const jobCloseDate = job.closed_at?.split('T')[0] || job.job_date;
            if (leadDate && jobCloseDate && leadDate.substring(0, 7) !== jobCloseDate.substring(0, 7)) {
                return {
                    job: enrichedJob,
                    category: 'lead_date_mismatch' as DiagnosticCategory,
                    reason: `Lead created ${leadDate}, Job closed ${jobCloseDate}. Campaign Report filters by lead creation date, so this job may be excluded from the report for the close month.`,
                };
            }

            return {
                job: enrichedJob,
                category: 'attributed' as DiagnosticCategory,
                reason: `Properly attributed to campaign: ${campaignName}`,
            };
        });

        // 4. Summarize
        const summaryCalc: Record<DiagnosticCategory, { count: number; revenue: number }> = {
            no_lead: { count: 0, revenue: 0 },
            no_campaign: { count: 0, revenue: 0 },
            lead_date_mismatch: { count: 0, revenue: 0 },
            attributed: { count: 0, revenue: 0 },
        };

        diagnostics.forEach(d => {
            summaryCalc[d.category].count++;
            summaryCalc[d.category].revenue += d.job.invoice_total || 0;
        });

        setResults(diagnostics);
        setSummary(summaryCalc);
        setLoading(false);
    };

    const getCategoryBadge = (cat: DiagnosticCategory) => {
        switch (cat) {
            case 'attributed':
                return <Badge variant="success"><CheckCircle className="h-3 w-3 mr-1" />Attributed</Badge>;
            case 'no_lead':
                return <Badge variant="destructive-soft"><XCircle className="h-3 w-3 mr-1" />No Lead</Badge>;
            case 'no_campaign':
                return <Badge variant="warning"><AlertTriangle className="h-3 w-3 mr-1" />No Campaign</Badge>;
            case 'lead_date_mismatch':
                return <Badge variant="warning"><AlertTriangle className="h-3 w-3 mr-1" />Date Mismatch</Badge>;
        }
    };

    const totalRevenue = results.reduce((sum, r) => sum + (r.job.invoice_total || 0), 0);
    const missingRevenue = results
        .filter(r => r.category !== 'attributed')
        .reduce((sum, r) => sum + (r.job.invoice_total || 0), 0);

    return (
        <Layout>
            <div className="container mx-auto p-6 space-y-6">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Revenue Attribution Diagnostic</h1>
                    <p className="text-muted-foreground mt-1">
                        Analyzes why some closed jobs don't appear in the Campaign Report
                    </p>
                </div>

                {/* Date Picker + Run */}
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-end gap-4">
                            <div>
                                <label className="text-sm font-medium mb-1 block">From</label>
                                <input
                                    type="date"
                                    className="border rounded px-3 py-2 text-sm"
                                    value={dateRange.from}
                                    onChange={e => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1 block">To</label>
                                <input
                                    type="date"
                                    className="border rounded px-3 py-2 text-sm"
                                    value={dateRange.to}
                                    onChange={e => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                                />
                            </div>
                            <Button onClick={runDiagnostic} disabled={loading}>
                                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                                Run Diagnostic
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Summary Cards */}
                {summary && (
                    <div className="grid gap-4 md:grid-cols-4">
                        <Card className="border-l-4 border-l-success">
                            <CardContent className="pt-6">
                                <div className="text-xs font-medium text-muted-foreground uppercase">Attributed (OK)</div>
                                <div className="text-2xl font-bold text-success">{summary.attributed.count} jobs</div>
                                <div className="text-sm text-muted-foreground">{formatMoney(summary.attributed.revenue)}</div>
                            </CardContent>
                        </Card>
                        <Card className="border-l-4 border-l-destructive">
                            <CardContent className="pt-6">
                                <div className="text-xs font-medium text-muted-foreground uppercase">No Lead Linked</div>
                                <div className="text-2xl font-bold text-destructive">{summary.no_lead.count} jobs</div>
                                <div className="text-sm text-muted-foreground">{formatMoney(summary.no_lead.revenue)}</div>
                            </CardContent>
                        </Card>
                        <Card className="border-l-4 border-l-warning">
                            <CardContent className="pt-6">
                                <div className="text-xs font-medium text-muted-foreground uppercase">No Campaign on Lead</div>
                                <div className="text-2xl font-bold text-warning">{summary.no_campaign.count} jobs</div>
                                <div className="text-sm text-muted-foreground">{formatMoney(summary.no_campaign.revenue)}</div>
                            </CardContent>
                        </Card>
                        <Card className="border-l-4 border-l-warning">
                            <CardContent className="pt-6">
                                <div className="text-xs font-medium text-muted-foreground uppercase">Date Mismatch</div>
                                <div className="text-2xl font-bold text-warning">{summary.lead_date_mismatch.count} jobs</div>
                                <div className="text-sm text-muted-foreground">{formatMoney(summary.lead_date_mismatch.revenue)}</div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Totals */}
                {summary && (
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex justify-between items-center">
                                <div>
                                    <span className="text-sm font-medium">Total Revenue (All Jobs): </span>
                                    <span className="text-lg font-bold">{formatMoney(totalRevenue)}</span>
                                </div>
                                <div>
                                    <span className="text-sm font-medium">Missing from Campaign Report: </span>
                                    <span className="text-lg font-bold text-destructive">{formatMoney(missingRevenue)}</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Detailed Table */}
                {results.length > 0 && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Job-by-Job Breakdown</CardTitle>
                            <CardDescription>
                                {results.length} jobs analyzed. Showing highest revenue first.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="rounded-md border overflow-auto max-h-[600px]">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Job #</TableHead>
                                            <TableHead>Customer</TableHead>
                                            <TableHead className="text-right">Revenue</TableHead>
                                            <TableHead>Closed At</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Lead Source</TableHead>
                                            <TableHead>Campaign</TableHead>
                                            <TableHead>Lead Created</TableHead>
                                            <TableHead>Diagnosis</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {results.map(r => (
                                            <TableRow key={r.job.id} className={r.category !== 'attributed' ? 'bg-destructive/5' : ''}>
                                                <TableCell className="font-mono text-xs">{r.job.job_number}</TableCell>
                                                <TableCell className="max-w-[150px] truncate">{r.job.customer_name}</TableCell>
                                                <TableCell className="text-right font-medium">
                                                    {formatMoney(r.job.invoice_total || 0)}
                                                </TableCell>
                                                <TableCell className="text-xs">{r.job.closed_at?.split('T')[0] || r.job.job_date || '-'}</TableCell>
                                                <TableCell>{getCategoryBadge(r.category)}</TableCell>
                                                <TableCell className="text-xs">{r.job.lead_source_name || '-'}</TableCell>
                                                <TableCell className="text-xs">{r.job.lead_campaign_name || '-'}</TableCell>
                                                <TableCell className="text-xs">{r.job.lead_created_at?.split('T')[0] || '-'}</TableCell>
                                                <TableCell className="text-xs max-w-[250px]">{r.reason}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
}
